import React, { useEffect, useState, useCallback } from 'react';
import {
  Bell,
  BellOff,
  CheckCircle2,
  AlertTriangle,
  Smartphone,
  Laptop,
  Trash2,
  Send,
  RefreshCw,
  ShieldCheck,
  Share,
  PlusSquare,
  Info,
} from 'lucide-react';
import {
  BrowserPushPermissionState,
  RegisteredPushDevice,
  UserPushStatusResponse,
  ensurePushServiceWorkerRegistered,
  fetchUserPushStatus,
  getBrowserPushPermissionState,
  getCurrentBrowserPushEndpoint,
  isIosDevice,
  isStandaloneWebApp,
  isWebPushSupportedInBrowser,
  removeRegisteredPushDevice,
  sendUserTestPushNotification,
  subscribeCurrentDeviceToPush,
  toggleUserPushPreference,
  unsubscribeCurrentDeviceFromPush,
} from '../../lib/webPushClient';

interface PushNotificationSettingsCardProps {
  userId: string;
  userRole: 'customer' | 'professional';
  pushEnabledPreference?: boolean;
  onPushPreferenceChange?: (enabled: boolean) => void;
}

export const PushNotificationSettingsCard: React.FC<PushNotificationSettingsCardProps> = ({
  userId,
  userRole,
  pushEnabledPreference,
  onPushPreferenceChange,
}) => {
  const [permissionState, setPermissionState] = useState<BrowserPushPermissionState>(() =>
    getBrowserPushPermissionState()
  );
  const [pushStatus, setPushStatus] = useState<UserPushStatusResponse | null>(null);
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionBusy, setActionBusy] = useState<boolean>(false);
  const [testBusy, setTestBusy] = useState<boolean>(false);
  const [removingDeviceId, setRemovingDeviceId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);

  const isIos = isIosDevice();
  const isStandalone = isStandaloneWebApp();
  const isSupported = isWebPushSupportedInBrowser();
  const needsIosHomeScreenInstall = isIos && !isStandalone;

  const refreshPushState = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setPermissionState(getBrowserPushPermissionState());
      if (isWebPushSupportedInBrowser()) {
        await ensurePushServiceWorkerRegistered();
      }
      const [statusData, browserEndpoint] = await Promise.all([
        fetchUserPushStatus(userId),
        getCurrentBrowserPushEndpoint(),
      ]);
      if (statusData) {
        setPushStatus(statusData);
      }
      setCurrentEndpoint(browserEndpoint);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void refreshPushState();
  }, [refreshPushState]);

  useEffect(() => {
    const handleUpdated = () => {
      void refreshPushState();
    };
    window.addEventListener('jobmatch:push-status-updated', handleUpdated);
    return () => {
      window.removeEventListener('jobmatch:push-status-updated', handleUpdated);
    };
  }, [refreshPushState]);

  const isThisDeviceSubscribed = Boolean(
    currentEndpoint &&
      pushStatus?.devices?.some(
        (d) =>
          d.isActive &&
          (d.endpoint === currentEndpoint ||
            (currentEndpoint.length > 24 &&
              d.endpointPreview.startsWith(currentEndpoint.slice(0, 28))))
      )
  );

  const effectivePushEnabled =
    pushEnabledPreference !== undefined
      ? pushEnabledPreference
      : (pushStatus?.pushEnabled ?? true);

  const handleEnableOnThisDevice = async () => {
    setActionBusy(true);
    setFeedback(null);
    try {
      const res = await subscribeCurrentDeviceToPush({
        userId,
        role: userRole,
      });
      setPermissionState(res.permission);
      if (res.success) {
        if (res.status) setPushStatus(res.status);
        if (res.endpoint) setCurrentEndpoint(res.endpoint);
        onPushPreferenceChange?.(true);
        setFeedback({
          type: 'success',
          text: 'Browser push notifications are now active on this device.',
        });
      } else {
        setFeedback({
          type: 'error',
          text: res.error || 'Could not enable push notifications on this browser.',
        });
      }
      await refreshPushState();
    } finally {
      setActionBusy(false);
    }
  };

  const handleDisableOnThisDevice = async () => {
    setActionBusy(true);
    setFeedback(null);
    try {
      const res = await unsubscribeCurrentDeviceFromPush(userId);
      if (res.status) setPushStatus(res.status);
      setCurrentEndpoint(null);
      if (res.success) {
        setFeedback({
          type: 'info',
          text: 'Push notifications have been disabled on this browser/device.',
        });
      } else {
        setFeedback({
          type: 'error',
          text: res.error || 'Failed to unsubscribe this device.',
        });
      }
      await refreshPushState();
    } finally {
      setActionBusy(false);
    }
  };

  const handleToggleAccountPushPreference = async () => {
    const nextVal = !effectivePushEnabled;
    setActionBusy(true);
    setFeedback(null);
    try {
      const res = await toggleUserPushPreference({
        userId,
        role: userRole,
        pushEnabled: nextVal,
      });
      if (res.success) {
        onPushPreferenceChange?.(nextVal);
        setPushStatus((prev) => (prev ? { ...prev, pushEnabled: nextVal } : prev));
        setFeedback({
          type: 'success',
          text: nextVal
            ? 'Account push alerts enabled across your registered devices.'
            : 'Account push alerts paused across all devices.',
        });
      } else {
        setFeedback({
          type: 'error',
          text: res.error || 'Failed to update push preference.',
        });
      }
    } finally {
      setActionBusy(false);
    }
  };

  const handleSendTestPush = async () => {
    setTestBusy(true);
    setFeedback(null);
    try {
      const res = await sendUserTestPushNotification({
        userId,
        role: userRole,
      });
      if (res.success) {
        setFeedback({
          type: 'success',
          text:
            res.message ||
            `Test push notification sent to ${res.deliveredCount} registered device(s).`,
        });
      } else {
        setFeedback({
          type: 'error',
          text:
            res.error ||
            'Could not deliver test push notification. Make sure this device is enabled first.',
        });
      }
      await refreshPushState();
    } finally {
      setTestBusy(false);
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    setRemovingDeviceId(deviceId);
    setFeedback(null);
    try {
      const res = await removeRegisteredPushDevice(userId, deviceId);
      if (res.status) setPushStatus(res.status);
      if (res.success) {
        setFeedback({
          type: 'info',
          text: 'Device removed from your push notification subscriptions.',
        });
      } else {
        setFeedback({
          type: 'error',
          text: res.error || 'Failed to remove device.',
        });
      }
      await refreshPushState();
    } finally {
      setRemovingDeviceId(null);
    }
  };

  const renderPermissionBadge = () => {
    if (needsIosHomeScreenInstall) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
          <AlertTriangle className="w-3.5 h-3.5" />
          Home Screen Install Required (iOS)
        </span>
      );
    }
    if (!isSupported || permissionState === 'unsupported') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
          <BellOff className="w-3.5 h-3.5" />
          Unsupported Browser
        </span>
      );
    }
    if (permissionState === 'granted') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Browser Permission: Granted
        </span>
      );
    }
    if (permissionState === 'denied') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-red-100 text-red-800 border border-red-200">
          <AlertTriangle className="w-3.5 h-3.5" />
          Browser Permission: Blocked
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-blue-50 text-blue-800 border border-blue-200">
        <Info className="w-3.5 h-3.5" />
        Permission: Not Requested Yet
      </span>
    );
  };

  const activeDevices: RegisteredPushDevice[] = (pushStatus?.devices || []).filter(
    (d) => d.isActive
  );

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
      data-testid="web-push-settings-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-5 border-b border-slate-100">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Bell className="w-5 h-5 text-emerald-600" />
              Browser Push Notifications (Android, iPhone &amp; Desktop)
            </h3>
            {renderPermissionBadge()}
          </div>
          <p className="text-sm text-slate-600 mt-1.5">
            {userRole === 'professional'
              ? 'Receive instant device alerts for new matching jobs in your LGA, customer messages, accepted quotes, job assignments, payouts, and verification updates — even when your browser tab is closed.'
              : 'Receive instant device alerts for new artisan quotes, chat messages, professional assignment, job status updates, and escrow payment/refund confirmations.'}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => void refreshPushState()}
            disabled={loading || actionBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-50"
            title="Refresh subscription status"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* iPhone / iOS 16.4+ Home Screen Installation Instructions */}
      {needsIosHomeScreenInstall && (
        <div
          className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900"
          data-testid="ios-homescreen-push-notice"
        >
          <div className="flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm space-y-2">
              <p className="font-bold">
                iPhone Setup Required: Install JobMatch NG on Your Home Screen First
              </p>
              <p className="text-amber-800 leading-relaxed">
                Apple iOS (16.4 or later) requires web apps to be added to your Home Screen before
                browser push notifications can be enabled:
              </p>
              <ol className="list-decimal list-inside space-y-1 font-medium text-amber-900">
                <li className="flex items-center gap-1.5">
                  <span>1. Tap the</span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-amber-300 text-xs font-bold">
                    <Share className="w-3.5 h-3.5 text-blue-600" /> Share
                  </span>
                  <span>button at the bottom of Safari.</span>
                </li>
                <li className="flex items-center gap-1.5">
                  <span>2. Scroll down and tap</span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-amber-300 text-xs font-bold">
                    <PlusSquare className="w-3.5 h-3.5 text-slate-700" /> Add to Home Screen
                  </span>
                  <span>.</span>
                </li>
                <li>
                  3. Open <strong>JobMatch NG</strong> from your iPhone Home Screen and tap{' '}
                  <strong>Enable Push Notifications</strong> below.
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Blocked Permission Guidance */}
      {permissionState === 'denied' && (
        <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-xs sm:text-sm">
          <p className="font-bold flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
            Notifications Are Blocked in Your Browser Settings
          </p>
          <p className="mt-1 text-red-800">
            To receive push alerts on this device, click the lock/tune icon next to{' '}
            <strong>jobmatch.ng</strong> in your browser address bar, change{' '}
            <strong>Notifications</strong> to <strong>Allow</strong>, and reload the page.
          </p>
        </div>
      )}

      {/* Feedback Banner */}
      {feedback && (
        <div
          className={`mt-4 p-3.5 rounded-xl border text-xs sm:text-sm font-medium flex items-center justify-between gap-3 ${
            feedback.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : feedback.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-900'
                : 'bg-slate-50 border-slate-200 text-slate-800'
          }`}
          data-testid="web-push-feedback"
        >
          <span>{feedback.text}</span>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            className="text-xs font-bold underline opacity-75 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Primary Controls */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* This Device Subscription Control */}
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between gap-3">
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                This Browser / Device
              </span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
                  isThisDeviceSubscribed
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-slate-200 text-slate-700'
                }`}
                data-testid="device-subscription-status"
              >
                {isThisDeviceSubscribed ? 'Subscribed & Active' : 'Not Subscribed'}
              </span>
            </div>
            <p className="text-xs text-slate-600 mt-1.5">
              {isThisDeviceSubscribed
                ? 'This device is registered to receive real-time JobMatch NG push notifications.'
                : 'Click Enable Notifications below to request browser permission and register this device.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            {!isThisDeviceSubscribed ? (
              <button
                type="button"
                onClick={() => void handleEnableOnThisDevice()}
                disabled={actionBusy || needsIosHomeScreenInstall}
                data-testid="enable-push-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors disabled:opacity-50"
              >
                <Bell className="w-4 h-4" />
                {actionBusy ? 'Enabling...' : 'Enable Notifications'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void handleDisableOnThisDevice()}
                disabled={actionBusy}
                data-testid="disable-push-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold bg-white hover:bg-red-50 text-red-700 border border-red-200 transition-colors disabled:opacity-50"
              >
                <BellOff className="w-4 h-4" />
                {actionBusy ? 'Disabling...' : 'Disable on This Device'}
              </button>
            )}

            <button
              type="button"
              onClick={() => void handleSendTestPush()}
              disabled={testBusy || activeDevices.length === 0}
              data-testid="send-test-push-btn"
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold bg-slate-900 hover:bg-slate-800 text-white transition-colors disabled:opacity-40"
            >
              <Send className="w-3.5 h-3.5" />
              {testBusy ? 'Sending...' : 'Send Test Notification'}
            </button>
          </div>
        </div>

        {/* Account-Level Push Preference & Multi-Device Summary */}
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex flex-col justify-between gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Account Push Delivery
              </span>
              <p className="text-sm font-bold text-slate-900 mt-1">
                {effectivePushEnabled ? 'Push Alerts Enabled' : 'Push Alerts Paused'}
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                {activeDevices.length} active device{activeDevices.length === 1 ? '' : 's'}{' '}
                registered to your account. Works alongside Email &amp; SMS.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void handleToggleAccountPushPreference()}
              disabled={actionBusy}
              data-testid="toggle-account-push-pref"
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                effectivePushEnabled
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              {effectivePushEnabled ? 'Enabled' : 'Paused'}
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t border-slate-200/70">
            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>
              OTPs, passwords, and full bank numbers are never included in push notifications.
            </span>
          </div>
        </div>
      </div>

      {/* Registered Devices List (Multi-Device Support) */}
      {activeDevices.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
            Registered Devices on Your Account ({activeDevices.length})
          </h4>
          <div className="space-y-2" data-testid="registered-push-devices-list">
            {activeDevices.map((dev) => {
              const isCurrent =
                Boolean(currentEndpoint) &&
                (dev.endpoint === currentEndpoint ||
                  (currentEndpoint &&
                    currentEndpoint.length > 24 &&
                    dev.endpointPreview.startsWith(currentEndpoint.slice(0, 28))));

              return (
                <div
                  key={dev.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {dev.platform === 'android' || dev.platform === 'ios' ? (
                      <Smartphone className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <Laptop className="w-4 h-4 text-slate-600 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900 truncate">{dev.deviceLabel}</span>
                        {isCurrent && (
                          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                            This Device
                          </span>
                        )}
                        {dev.isStandalonePwa && (
                          <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-bold">
                            Home Screen App
                          </span>
                        )}
                      </div>
                      <p className="text-slate-500 truncate mt-0.5">
                        Added {new Date(dev.createdAt).toLocaleDateString('en-NG')}
                        {dev.lastDeliveredAt
                          ? ` • Last alert ${new Date(dev.lastDeliveredAt).toLocaleTimeString('en-NG', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}`
                          : ''}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => void handleRemoveDevice(dev.id)}
                    disabled={removingDeviceId === dev.id}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors shrink-0 disabled:opacity-50"
                    title="Remove device"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>{removingDeviceId === dev.id ? 'Removing...' : 'Remove'}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
